import React from 'react'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { createListLinkTarget } from '../utils/listPageState'
import { adminRadii } from '../theme/adminTokens'

export const FORM_ACTION_BUTTON_BASE_SX = {
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': {
        mr: 0.75,
    },
}

// 등록/수정 화면의 기본 이미지 업로드 버튼에 사용합니다.
export const FORM_UPLOAD_BUTTON_SX = {
    ...FORM_ACTION_BUTTON_BASE_SX,
    height: 34,
    minWidth: 128,
    px: 1.5,
}

// 메인이미지, 상세이미지, 배너 이미지처럼 라벨이 긴 업로드 버튼에 사용합니다.
export const FORM_WIDE_UPLOAD_BUTTON_SX = {
    ...FORM_UPLOAD_BUTTON_SX,
    minWidth: 154,
}

// 이미지 업로드 패널에서 선택한 파일만 비우는 버튼에 사용합니다.
export const FORM_IMAGE_CLEAR_BUTTON_SX = {
    minWidth: 0,
    justifySelf: 'end',
    px: 0.75,
    fontSize: '0.75rem',
    lineHeight: 1,
}

// 테이블이나 피커 안의 짧은 선택/취소 버튼에 사용합니다.
export const FORM_COMPACT_ACTION_BUTTON_SX = {
    ...FORM_ACTION_BUTTON_BASE_SX,
    height: 28,
    minWidth: 54,
    px: 1.25,
}

// 테이블, 상세 액션 영역의 위험/주의 버튼에 사용합니다.
export const FORM_COMPACT_DANGER_ACTION_BUTTON_SX = {
    ...FORM_COMPACT_ACTION_BUTTON_SX,
    minWidth: 88,
}

export const FORM_COMPACT_WARNING_ACTION_BUTTON_SX = {
    ...FORM_COMPACT_ACTION_BUTTON_SX,
    minWidth: 88,
}

// 입력 필드 옆에 붙는 수정/취소/저장 버튼에 사용합니다.
export const FORM_INLINE_ACTION_BUTTON_SX = {
    ...FORM_ACTION_BUTTON_BASE_SX,
    height: 34,
    minWidth: 72,
    px: 1.5,
}

// 확인 모달 하단의 취소/실행 버튼에 사용합니다.
export const FORM_DIALOG_ACTION_BUTTON_SX = {
    ...FORM_ACTION_BUTTON_BASE_SX,
    height: 36,
    minWidth: 116,
    px: 2,
}

export const FORM_DIALOG_DANGER_ACTION_BUTTON_SX = {
    ...FORM_DIALOG_ACTION_BUTTON_SX,
    minWidth: 118,
}

export const FORM_DIALOG_WARNING_ACTION_BUTTON_SX = {
    ...FORM_DIALOG_ACTION_BUTTON_SX,
    minWidth: 118,
}

export const BUTTON_LOADING_ICON = <CircularProgress size={16} color="inherit" />

export default function BottomButtonGroup(props) {
    const hasBackPath = Boolean(props.path)
    const backLabel = props.backLabel || '뒤로'
    const actionVariant = props.actionVariant || 'contained'
    const actionColor = props.actionColor || 'secondary'
    const backTarget = hasBackPath
        ? createListLinkTarget({
            pathname: props.path,
            search: props.search,
            curPage: props.curPage,
            searchText: props.searchText,
            searchSelect: props.searchSelect,
            startTime: props.startTime,
            endTime: props.endTime,
        })
        : null

    if (props.variant === 'emptyStateAction') {
        return hasBackPath ? (
            <Button component={Link} to={backTarget} variant={actionVariant} color={actionColor} disabled={props.backDisabled} onClick={props.onBackClick}>{backLabel}</Button>
        ) : (
            <Button type="button" variant={actionVariant} color={actionColor} disabled={props.backDisabled} onClick={props.onBackClick}>{backLabel}</Button>
        )
    }

    const saveButtonVisible = props.variant === 'formAction' ? props.visible !== false : props.visible
    const shouldUseFormAction = props.variant === 'formAction' || saveButtonVisible
    const submitLabel = props.submitLabel || '저장'
    const extraActions = props.extraActions || null
    const submitLoading = props.submitLoading ?? (
        typeof submitLabel === 'string' &&
        submitLabel.includes('중')
    )
    const actionDescription = props.actionDescription || (
        props.search || props.curPage || props.searchText || props.searchSelect
            ? '저장 후 이전 목록 위치로 돌아갑니다.'
            : '저장 후 이전 화면으로 돌아갑니다.'
    )

    if (shouldUseFormAction) {
        return (
            <Paper
                variant="outlined"
                sx={{
                    mt: 2.5,
                    p: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    borderRadius: `${adminRadii.control}px`,
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                }}
            >
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {props.actionTitle || '변경사항 저장'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {actionDescription}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        gap: 1,
                        width: { xs: '100%', sm: 'auto' },
                        '& .MuiButton-root': {
                            minWidth: { xs: '100%', sm: 108 },
                        },
                    }}
                >
                    {hasBackPath ? (
                        <Button component={Link} to={backTarget} variant="outlined" color="secondary" disabled={props.backDisabled} onClick={props.onBackClick}>{backLabel}</Button>
                    ) : (
                        <Button type="button" variant="outlined" color="secondary" disabled={props.backDisabled} onClick={props.onBackClick}>{backLabel}</Button>
                    )}
                    {saveButtonVisible &&
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={props.submitDisabled}
                        startIcon={submitLoading ? BUTTON_LOADING_ICON : props.submitStartIcon}
                        aria-busy={submitLoading}
                    >
                        {submitLabel}
                    </Button>
                    }
                    {extraActions}
                </Box>
            </Paper>
        )
    }

    return (
        <Box 
            display="flex" 
            flexDirection="row"
            alignItems="center"
            justifyContent="center" 
            sx={{ '& button': { m: 2 } }}
        >
            {hasBackPath ? (
                <Button component={Link} to={backTarget} variant="outlined" color="secondary" disabled={props.backDisabled} onClick={props.onBackClick}>{backLabel}</Button>
            ) : (
                <Button type="button" variant="outlined" color="secondary" disabled={props.backDisabled} onClick={props.onBackClick}>{backLabel}</Button>
            )}
            {saveButtonVisible &&
            <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={props.submitDisabled}
                startIcon={submitLoading ? BUTTON_LOADING_ICON : props.submitStartIcon}
                aria-busy={submitLoading}
            >
                {submitLabel}
            </Button>
            }
        </Box>
    )
}
